/**
 * TARA Workflow v2 — reducer.
 *
 * Single-reducer state model replacing the legacy 5-context substrate.
 * The backend owns per-step data state; the reducer is the projection
 * of that truth + per-step UI flags (draft text, message thread). On
 * mount and on every polling tick, `HYDRATE_FROM_BACKEND` absorbs the
 * latest `WorkflowProgress` snapshot from `GET /workflow/{tid}/status`
 * and writes it into the reducer.
 *
 * Per-step status (6 values) maps the backend's 2×3 grid
 * (AgentStatus × AgentReviewStatus) onto one user-visible label:
 *
 *   empty       (status=pending,     review=pending)         — agent hasn't run yet
 *   generating  (status=in_progress, review=pending)         — agent doing its first run
 *   draft       (status=completed,   review=awaiting_review) — output ready, awaiting user
 *   revising    (status=in_progress, review=awaiting_review) — agent re-running after revise
 *   approved    (status=completed,   review=approved)        — user signed off
 *   failed      (status=failed,      review=*)               — agent crashed
 *
 * The reducer never originates status; `deriveStatus` is the only
 * place those values are computed, and only `HYDRATE_FROM_BACKEND`
 * invokes it. UI actions (`HYDRATE_CHAT`, `SEND_USER_MESSAGE`,
 * etc.) never touch `status` so polling updates never fight chrome
 * state.
 *
 * Sticky-approved guard: once a step's status is `approved`, hydrate
 * keeps it there even if a follow-up snapshot momentarily returns
 * `awaiting_review` (defends against backend write-lag flapping).
 */

import { STEP_CATALOG } from './workflowV2StepCatalog';
import type {
  ChatMessage,
  HierarchicalModifications,
  PerStepState,
  StepStatus,
  WorkflowV2State,
} from './workflowV2Types';

/**
 * Pure derivation — backend (status, review_status) → frontend status.
 * Exported so tests + tooling can use it directly.
 */
export function deriveStatus(input: { status?: string; review_status?: string } | null | undefined): StepStatus {
  if (!input) return 'empty';
  const { status, review_status } = input;
  if (status === 'failed') return 'failed';
  if (status === 'pending') return 'empty';
  if (status === 'in_progress') {
    return review_status === 'awaiting_review' ? 'revising' : 'generating';
  }
  if (status === 'completed') {
    return review_status === 'approved' ? 'approved' : 'draft';
  }
  return 'empty';
}

const buildStepInitial = (id: number): PerStepState => ({
  id,
  agentName: STEP_CATALOG.find((s) => s.id === id)?.agent ?? null,

  // Backend-derived (hydrate writes these)
  status: 'empty',
  result: null,
  startedAt: null,
  completedAt: null,
  userApprovedAt: null,
  errorMessage: null,

  // Pure UI (per-step, survives step switches; never touched by hydrate).
  // Chat is now always rendered (post Story 15.A2) — no chatOpen flag.
  // `agentTyping` removed; ActionPanel derives it from status === 'revising'.
  draft: '',
  messages: [],

  // Step-0 wizard sub-mode. null = chooser visible; one of
  // 'automatic' | 'manual' | 'diagram' once user picks a card.
  // Other steps ignore this field. Never touched by hydrate.
  selectedMode: null,

  // Hierarchical Child*Table relevance modifications (Story H2).
  // Lifted from useState in `useHierarchicalApprove` so the map
  // survives step navigation. Cleared after a successful approve.
  // Non-hierarchical steps just never write to it.
  hierarchicalModifications: {},
});

export const initialState: WorkflowV2State = {
  // New projects land on Step 0 (upload). WorkflowV2.tsx bumps this
  // to Step 1 on mount if the project already has a thread_id, OR
  // after Step 0 successfully transitions to `approved`.
  selectedStepId: 0,
  // Becomes true once every agent-backed step is `approved`. Mirrors
  // legacy `WorkflowStatus === COMPLETED` — when true, CRUD opens up
  // on all editable steps (2-8); before that, only the step currently
  // `draft` is editable.
  workflowCompleted: false,
  steps: STEP_CATALOG.reduce<Record<number, PerStepState>>((acc, s) => {
    acc[s.id] = buildStepInitial(s.id);
    return acc;
  }, {}),
};

let _msgSeq = 0;
const nextMsgId = (): string => `m-${Date.now()}-${++_msgSeq}`;

export const ACTIONS = Object.freeze({
  SELECT_STEP: 'SELECT_STEP',
  HYDRATE_FROM_BACKEND: 'HYDRATE_FROM_BACKEND',
  // Used for agentless steps (e.g. Step 0 upload) whose status is
  // driven by the frontend, not by HYDRATE_FROM_BACKEND. Payload:
  // `{ stepId, status, error? }`.
  SET_STEP_STATUS: 'SET_STEP_STATUS',
  SET_STEP_RESULT: 'SET_STEP_RESULT',
  // Step-0 wizard: selects which of the three input modes is active
  // (automatic / manual / diagram). null resets to the chooser view.
  SET_STEP_MODE: 'SET_STEP_MODE',
  UPDATE_DRAFT: 'UPDATE_DRAFT',
  SEND_USER_MESSAGE: 'SEND_USER_MESSAGE',
  RECEIVE_AGENT_REPLY: 'RECEIVE_AGENT_REPLY',
  // Replaces the per-step messages array with a backend snapshot.
  // Used by useChatPersistence on initial fetch + after a refresh.
  HYDRATE_CHAT: 'HYDRATE_CHAT',
  // Hierarchical-mode child-record relevance modifications (Story H2).
  // Pure UI state — survives step navigation, cleared after a
  // successful `approveHierarchicalData` save.
  SET_HIER_RELEVANCE: 'SET_HIER_RELEVANCE',
  SET_HIER_JUSTIFICATION: 'SET_HIER_JUSTIFICATION',
  CLEAR_HIER_MODIFICATIONS: 'CLEAR_HIER_MODIFICATIONS',
  RESET_STEP: 'RESET_STEP',
  RESET_ALL: 'RESET_ALL',
} as const);

// Loose `any` action typing — the reducer's switch handles each
// type's payload shape explicitly. Tightening to a discriminated
// union is a future refactor; current consumers dispatch via the
// `createWorkflowActions` helper which is typed.
type WorkflowAction = { type: string; [key: string]: any };

export function workflowReducer(state: WorkflowV2State, action: WorkflowAction): WorkflowV2State {
  const cur: PerStepState | null = action.stepId != null ? state.steps[action.stepId] : null;

  switch (action.type) {
    case ACTIONS.SELECT_STEP:
      return { ...state, selectedStepId: action.stepId };

    case ACTIONS.HYDRATE_FROM_BACKEND: {
      // Walk each step in the catalog, look up its agent's slice in
      // workflow_progress.agent_progress, derive the new status, and
      // copy backend fields. Sticky-approved: never demote.
      //
      // Agentless steps (e.g. Step 0 upload) are skipped — their
      // status is owned by SET_STEP_STATUS, not by the polling tick.
      // Accept both shapes:
      //   - DB document   → `agent_progress` (workflow_progress collection)
      //   - API response  → `agent_details`  (AsyncWorkflowStatusResponse)
      // useWorkflowStatusPolling feeds us the API response, so
      // `agent_details` is the live key — but tests + future callers
      // may pass the DB shape directly.
      const wp = action.workflowProgress || {};
      const agentProgress = wp.agent_details || wp.agent_progress || {};
      const nextSteps: Record<number, PerStepState> = { ...state.steps };

      for (const step of STEP_CATALOG) {
        if (step.agent == null) continue; // skip agentless steps (Step 0)
        const slot = nextSteps[step.id];
        if (!slot) continue;
        const ap = agentProgress[slot.agentName as string];
        if (!ap) continue;

        const derived = deriveStatus({
          status: ap.status,
          review_status: ap.review_status,
        });

        // Status reconciliation guards (in priority order):
        //   1. Sticky approved — once approved, never revert via hydrate
        //      (defends against backend write-lag flapping).
        //   2. Sticky generating — once an upstream flow has
        //      optimistically written `generating` (e.g. useUploadFlow,
        //      useApproveFlow's next-step write), don't let a polling
        //      tick demote to `empty` just because the backend agent
        //      hasn't started in_progress yet. LangGraph kicks off
        //      asynchronously so there's a brief window (~100-500ms)
        //      where the agent_progress slot is still pending+pending.
        //      Legitimate progression (`draft`, `revising`, `approved`,
        //      `failed`) still applies — only the `empty` demotion is
        //      blocked. Without this guard the spinner blinks off
        //      between the optimistic write and the first real status.
        let nextStatus = derived;
        if (slot.status === 'approved' && derived !== 'approved') {
          nextStatus = 'approved';
        } else if (slot.status === 'generating' && derived === 'empty') {
          nextStatus = 'generating';
        }

        nextSteps[step.id] = {
          ...slot,
          status: nextStatus,
          result: ap.result ?? slot.result,
          startedAt: ap.started_at ?? slot.startedAt,
          completedAt: ap.completed_at ?? slot.completedAt,
          userApprovedAt: ap.user_approved_at ?? slot.userApprovedAt,
          errorMessage: ap.error_message ?? null,
        };
      }

      // Derive workflowCompleted: true iff every user-approvable step
      // (Step 1 through Step 8) is approved. Step 0 (upload) and Step 9
      // (final report — report agent is currently disabled in the
      // backend pipeline and isn't user-approved) are excluded.
      const workflowCompleted = STEP_CATALOG
        .filter((s) => s.agent != null && s.id >= 1 && s.id <= 8)
        .every((s) => nextSteps[s.id]?.status === 'approved');

      return { ...state, steps: nextSteps, workflowCompleted };
    }

    case ACTIONS.SET_STEP_RESULT: {
      // Used by the Step 1 getItemDefinition fetch (and similar
      // per-step structured re-fetches). Touches `result` only —
      // status changes come from hydrate, not from here.
      if (!cur) return state;
      return {
        ...state,
        steps: {
          ...state.steps,
          [action.stepId]: { ...cur, result: action.result, errorMessage: null },
        },
      };
    }

    case ACTIONS.SET_STEP_STATUS: {
      // Frontend-driven status writes for agentless steps. Step 0
      // uses this to flip empty → generating → approved | failed as
      // the upload flow progresses. The polling tick won't fight
      // these writes because HYDRATE_FROM_BACKEND skips agentless
      // steps.
      if (!cur) return state;
      return {
        ...state,
        steps: {
          ...state.steps,
          [action.stepId]: {
            ...cur,
            status: action.status,
            errorMessage: action.error ?? null,
          },
        },
      };
    }

    case ACTIONS.SET_STEP_MODE: {
      // Step-0 wizard: write the active sub-mode (or null to reset
      // to the chooser view). Never touched by HYDRATE so this is
      // pure UI state.
      if (!cur) return state;
      return {
        ...state,
        steps: {
          ...state.steps,
          [action.stepId]: {
            ...cur,
            selectedMode: action.mode ?? null,
          },
        },
      };
    }

    case ACTIONS.HYDRATE_CHAT: {
      // Replace the step's messages array with a backend snapshot.
      // Used by useChatPersistence on initial mount + on refresh so
      // the chat reflects the persisted thread. action.messages is
      // expected to be a list of normalized message objects
      // (id, role, text, timestamp) — the hook handles the API → UI
      // shape mapping.
      if (!cur) return state;
      const incoming: ChatMessage[] = Array.isArray(action.messages) ? action.messages : [];
      return {
        ...state,
        steps: {
          ...state.steps,
          [action.stepId]: { ...cur, messages: incoming },
        },
      };
    }

    case ACTIONS.UPDATE_DRAFT:
      if (!cur) return state;
      return {
        ...state,
        steps: {
          ...state.steps,
          [action.stepId]: { ...cur, draft: action.draft },
        },
      };

    case ACTIONS.SEND_USER_MESSAGE: {
      if (!cur) return state;
      const text = (cur.draft || '').trim();
      if (!text) return state;
      const userMsg: ChatMessage = {
        id: nextMsgId(),
        role: 'user',
        text,
        timestamp: new Date().toISOString(),
      };
      return {
        ...state,
        steps: {
          ...state.steps,
          [action.stepId]: {
            ...cur,
            draft: '',
            messages: [...cur.messages, userMsg],
          },
        },
      };
    }

    case ACTIONS.RECEIVE_AGENT_REPLY: {
      if (!cur) return state;
      const agentMsg: ChatMessage = {
        id: nextMsgId(),
        role: 'agent',
        text: action.text,
        timestamp: new Date().toISOString(),
      };
      return {
        ...state,
        steps: {
          ...state.steps,
          [action.stepId]: {
            ...cur,
            messages: [...cur.messages, agentMsg],
          },
        },
      };
    }

    case ACTIONS.SET_HIER_RELEVANCE: {
      if (!cur) return state;
      const recordId = String(action.recordId);
      const prev = cur.hierarchicalModifications[recordId] || {};
      return {
        ...state,
        steps: {
          ...state.steps,
          [action.stepId]: {
            ...cur,
            hierarchicalModifications: {
              ...cur.hierarchicalModifications,
              [recordId]: { ...prev, is_relevant: action.isRelevant },
            },
          },
        },
      };
    }

    case ACTIONS.SET_HIER_JUSTIFICATION: {
      if (!cur) return state;
      const recordId = String(action.recordId);
      const prev = cur.hierarchicalModifications[recordId] || {};
      return {
        ...state,
        steps: {
          ...state.steps,
          [action.stepId]: {
            ...cur,
            hierarchicalModifications: {
              ...cur.hierarchicalModifications,
              [recordId]: { ...prev, relevance_justification: action.justification },
            },
          },
        },
      };
    }

    case ACTIONS.CLEAR_HIER_MODIFICATIONS: {
      if (!cur) return state;
      return {
        ...state,
        steps: {
          ...state.steps,
          [action.stepId]: { ...cur, hierarchicalModifications: {} },
        },
      };
    }

    case ACTIONS.RESET_STEP:
      return {
        ...state,
        steps: { ...state.steps, [action.stepId]: buildStepInitial(action.stepId) },
      };

    case ACTIONS.RESET_ALL:
      return { ...initialState };

    default:
      return state;
  }
}

/**
 * Strict-sequential gating:
 *   - Step 0 (upload) is never locked — it's the entry point.
 *   - Step N (N≥1) is locked until step N-1 is approved.
 * With Step 0 in the catalog, Step 1's lock automatically reads
 * `state.steps[0]?.status !== 'approved'` (i.e. requires upload).
 */
export const isStepLocked = (state: WorkflowV2State, stepId: number): boolean => {
  if (stepId === 0) return false;
  return state.steps[stepId - 1]?.status !== 'approved';
};

/** Map status → StatusPill tone for the StepShell title pill. */
export const stepStatusVariant = (status: StepStatus): 'success' | 'warning' | 'info' | 'error' | 'neutral' => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'draft':
      return 'warning';
    case 'generating':
    case 'revising':
      return 'info';
    case 'failed':
      return 'error';
    default:
      return 'neutral';
  }
};

/** Map status → human label for the StepShell title pill. */
export const stepStatusLabel = (status: StepStatus): string => {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'draft':
      return 'Awaiting Review';
    case 'generating':
      return 'Generating…';
    case 'revising':
      return 'Revising…';
    case 'failed':
      return 'Failed';
    default:
      return 'Pending';
  }
};

/**
 * Step has agent output worth reviewing. Drives whether
 * WorkflowV2ActionPanel renders the chat thread (vs. an "Agent hasn't
 * produced output yet" placeholder).
 *
 * Story H6 — exported as an exhaustive switch so adding a new
 * StepStatus to the union forces a TS error here, rather than
 * silently flowing through the old hand-maintained Set in the panel.
 */
export const hasOutput = (status: StepStatus): boolean => {
  switch (status) {
    case 'draft':
    case 'revising':
    case 'approved':
      return true;
    case 'empty':
    case 'generating':
    case 'failed':
      return false;
  }
  // Exhaustiveness gate: if a new StepStatus is added without a
  // case branch above, this assignment errors at compile time.
  const _exhaustive: never = status;
  return _exhaustive;
};

/**
 * Approve & Proceed button is disabled in every state except `draft`.
 * Story H6 — exhaustive switch for the same compile-time-safety
 * reason as `hasOutput`. Does NOT incorporate the `approving`
 * in-flight flag — the consumer adds that via
 * `approveDisabledForStatus(status) || approving`.
 */
export const approveDisabledForStatus = (status: StepStatus): boolean => {
  switch (status) {
    case 'draft':
      return false;
    case 'empty':
    case 'generating':
    case 'revising':
    case 'approved':
    case 'failed':
      return true;
  }
  const _exhaustive: never = status;
  return _exhaustive;
};
